import React from 'react';
import {
  ActionIcon,
  Button,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import DynamicLayoutItem from './DynamicLayoutItem';
import { useDynamicUI } from '../DynamicUiContext';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { UiSection } from 'src/UI/dynamicUI/types';
import { useDisclosure } from '@mantine/hooks';
import { AutoUpdateIndicator } from 'src/financial/financial-data/financial-item/forms/common/AutoUpdateIndicator';

interface DynamicSectionProps {
  section: UiSection;
}

// Don't use this component! It will soon be deprecated.
export default function DeprecatedDynamicEntity({ section }: DynamicSectionProps) {
  const { renderUiEntityTitle } = useDynamicUI();

  const title = renderUiEntityTitle ? (
    renderUiEntityTitle(section)
  ) : (
    <Text fw={600} size="lg" data-testid="section-label">
      {section.label}
    </Text>
  );

  return (
    <FormContainer title={title}>
      <Stack gap="xxl">
        {section.children.map((item, i) => (
          <DynamicLayoutItem key={i} item={item} />
        ))}
      </Stack>
    </FormContainer>
  );
}

export type FormContainerProps = {
  children?: React.ReactNode;
  title: string | React.ReactNode;
};

export const FormContainer = ({ title, children }: FormContainerProps) => {
  const renderedTitle =
    typeof title === 'string' ? (
      <Text fw={600} size="lg" data-testid="section-label">
        {title}
      </Text>
    ) : (
      title
    );

  return (
    <>
      <Group justify="space-between" mb="md">
        {renderedTitle}
        <SectionActions />
      </Group>
      {children}
    </>
  );
};

function SectionActions() {
  const { form, isEditable, isEditing, isCreateMode, toggleEditing, onDelete } = useDynamicUI();

  const [warningModalOpened, { open: openWarningModal, close: closeWarningModal }] =
    useDisclosure(false);
  const theme = useMantineTheme();

  // Check if any product in the form is automatically updated
  const formValues = form.getValues();
  const isAutomaticallyUpdated = Object.values(formValues).some(productEntity => {
    return (
      productEntity &&
      typeof productEntity === 'object' &&
      'is_updated_automatically' in productEntity &&
      productEntity.is_updated_automatically === 'true'
    );
  });

  const pencilIcon = <IconPencil size={20} />;
  const saveButton = (
    <Button
      data-testid="save-button"
      disabled={!form.isDirty()}
      leftSection={pencilIcon}
      type="submit"
      loading={form.submitting}
    >
      Save
    </Button>
  );

  if (isCreateMode) {
    return saveButton;
  }

  if (!isEditable) {
    return form.isDirty() ? saveButton : null;
  }

  return (
    <Group>
      {isAutomaticallyUpdated && <AutoUpdateIndicator />}
      <Modal opened={warningModalOpened} onClose={closeWarningModal} title="Are you sure?" centered>
        <Text c="gray.10">Once delete you cannot recover this item.</Text>
        <Flex justify="flex-end" mt="xl">
          <Button variant="pill" onClick={onDelete} type="button" mr="xs">
            Yes
          </Button>
          <Button variant="pill" onClick={closeWarningModal}>
            No
          </Button>
        </Flex>
      </Modal>
      {isEditing && saveButton}
      <Button
        variant="default"
        onClick={toggleEditing}
        leftSection={!isEditing && pencilIcon}
        data-testid="toggle-edit-button"
      >
        {isEditing ? 'Cancel' : 'Edit'}
      </Button>
      {onDelete && (
        <ActionIcon
          onClick={openWarningModal}
          size={36}
          variant="default"
          color="orange"
          aria-label="Settings"
        >
          <IconTrash color={theme.colors.error[7]} />
        </ActionIcon>
      )}
    </Group>
  );
}
